/* Purpose: align variable title/detail/review content without truncating text; preview
 * localized swatch media and refresh selected upsell prices from server HTML.
 * Usage: load once through mvk-card-loader. Delegation also covers AJAX cards.
 * Scope: .mvk-card only. Existing variant selection/cart handlers remain intact;
 * there is no currency parsing, conversion or commerce/network request here.
 */
(() => {
  if (window.mvkProductCardsLoaded) return;
  window.mvkProductCardsLoaded = true;
  let frame;
  const schedule = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(() => { syncReviews(); align(); }); };
  // Purpose: summarize genuine Judge.me data without changing the app globally.
  // Standard review metafields render on the server; this fallback also covers
  // late badge updates and products whose standard metafields are not populated.
  function syncReviews() {
    const locale = document.documentElement.lang || 'en';
    const ratingFormat = new Intl.NumberFormat(locale, {minimumFractionDigits: 1, maximumFractionDigits: 1});
    const countFormat = new Intl.NumberFormat(locale);
    document.querySelectorAll('.mvk-card [data-mvk-review]').forEach(wrap => {
      if (wrap.dataset.mvkReviewMode !== 'summary') return;
      const badge = wrap.querySelector('.jdgm-prev-badge[data-average-rating][data-number-of-reviews]');
      if (!badge) return; // Keep server summary or original app fallback intact.
      const rating = Number(badge.dataset.averageRating), count = Number(badge.dataset.numberOfReviews);
      const max = Number(wrap.dataset.mvkReviewMax) || 5;
      if (!Number.isInteger(count) || count < 0 || !Number.isFinite(rating) || rating < 0 || rating > max) return;
      const signature = [rating, count, max, locale].join('|');
      if (wrap.dataset.mvkReviewSignature === signature) return;
      const summary = wrap.querySelector('.mvk-card-review__summary');
      if (!summary) return;
      wrap.dataset.mvkReviewSignature = signature;
      const score = ratingFormat.format(rating), total = countFormat.format(count);
      summary.querySelector('[data-mvk-rating]').textContent = score;
      summary.querySelector('[data-mvk-review-count]').textContent = '(' + total + ')';
      summary.setAttribute('aria-label', `${score} / ${max} (${total} ${wrap.dataset.mvkReviewLabel})`);
      summary.hidden = count === 0;
      wrap.setAttribute('data-mvk-review-ready', '');
    });
  }
  // Purpose: Swiper can resize slides after the window resize callback or after
  // lazy initialization. Re-align on actual card-width changes, not height changes
  // made by our own equalization, so this cannot create a resize feedback loop.
  const observedWidths = new Map();
  const widthObserver = new ResizeObserver(entries => {
    let changed = false;
    entries.forEach(({target, contentRect}) => {
      if (observedWidths.get(target) !== contentRect.width) {
        observedWidths.set(target, contentRect.width); changed = true;
      }
    });
    if (changed) schedule();
  });
  function align() {
    observedWidths.forEach((value, card) => {
      if (!card.isConnected) { widthObserver.unobserve(card); observedWidths.delete(card); }
    });
    document.querySelectorAll('.mvk-card:not(.mvk-card--compact)').forEach(card => {
      if (!observedWidths.has(card)) { observedWidths.set(card, null); widthObserver.observe(card); }
    });
    const cards = [...document.querySelectorAll('.mvk-card:not(.mvk-card--compact)')].filter(e => e.getBoundingClientRect().width > 0);
    // Clear prior measurements before measuring natural content after a resize.
    cards.forEach(e => ['--mvk-card-title-slot','--mvk-card-detail-slot','--mvk-card-review-slot','--mvk-card-price-offset'].forEach(k => e.style.removeProperty(k)));
    const groups = new Map();
    cards.forEach(card => {
      const parent = card.closest('.swiper-wrapper,.grid');
      if (!parent) return;
      if (!groups.has(parent)) groups.set(parent, new Map());
      const top = parent.classList.contains('swiper-wrapper') ? 0 : Math.round(card.offsetTop / 3) * 3;
      const rows = groups.get(parent); if (!rows.has(top)) rows.set(top, []); rows.get(top).push(card);
    });
    groups.forEach(rows => rows.forEach(row => {
      for (const [selector, property, minimum] of [['.mvk-card-title','--mvk-card-title-slot',0],['.mvk-card-details','--mvk-card-detail-slot',0],['.mvk-card-review','--mvk-card-review-slot',0]]) {
        const max = Math.max(minimum,...row.map(e => e.querySelector(selector)?.getBoundingClientRect().height || 0));
        row.forEach(e => e.style.setProperty(property, Math.ceil(max)+'px'));
      }
      // Measure the visible price, not its flex row: a large configured swatch
      // may raise the row's baseline. Keep prices aligned across those cards too.
      const prices = row.map(card => ({card,node:card.querySelector('.mvk-card-price')})).filter(x => x.node);
      const max = Math.max(0,...prices.map(x => x.node.getBoundingClientRect().top-x.card.getBoundingClientRect().top));
      prices.forEach(({card,node}) => card.style.setProperty('--mvk-card-price-offset', Math.max(0,max-(node.getBoundingClientRect().top-card.getBoundingClientRect().top))+'px'));
    }));
  }
  function preview(event, remove=false) {
    const swatch = event.target.closest?.('[data-mvk-swatch]'); if (!swatch) return;
    if (event.relatedTarget && swatch.contains(event.relatedTarget)) return;
    const image = swatch.closest('.mvk-card')?.querySelector('[data-mvk-swatch-preview]'); if (!image) return;
    image.style.backgroundImage = remove || !swatch.dataset.variantImage ? '' : `url(${JSON.stringify(swatch.dataset.variantImage)})`;
  }
  document.addEventListener('mouseover', e => preview(e));
  document.addEventListener('mouseout', e => preview(e,true));
  document.addEventListener('focusin', e => preview(e));
  document.addEventListener('focusout', e => preview(e,true));
  document.addEventListener('change', event => {
    const select = event.target.closest?.('.mvk-card product-grid-form select'); if (!select) return;
    const card = select.closest('.mvk-card'), id = select.selectedOptions[0]?.dataset.variantId;
    const template = [...card.querySelectorAll('template[data-mvk-variant]')].find(t => t.dataset.mvkVariant === id);
    if (!template) return;
    const content = template.content.cloneNode(true);
    const price = card.querySelector('[data-mvk-price]');
    const newPrice = content.querySelector('[data-mvk-price]');
    if (price && newPrice) price.replaceWith(newPrice);
    card.querySelector('[data-mvk-discount]')?.remove();
    const badge = content.querySelector('[data-mvk-discount]');
    if (badge) card.querySelector('.grid-product__image-mask')?.append(badge);
    schedule();
  });
  // Observe content changes, including Judge.me and section re-rendering. Observe only review-data attributes, ignoring
  // our own style/signature attributes so alignment does not create an observer loop.
  new MutationObserver(records => {
    if (records.some(r => r.target.parentElement?.closest('.mvk-card') || r.target.closest?.('.mvk-card') || [...r.addedNodes].some(n => n.matches?.('.mvk-card') || n.querySelector?.('.mvk-card')))) schedule();
  }).observe(document.body,{childList:true,subtree:true,characterData:true,attributes:true,attributeFilter:['data-average-rating','data-number-of-reviews']});
  window.addEventListener('resize',schedule);
  document.addEventListener('load',e => { if (e.target.closest?.('.mvk-card')) schedule(); },true);
  document.addEventListener('shopify:section:load',schedule);
  document.fonts?.ready.then(schedule);
  schedule();
})();
